import React, { useCallback } from "react";
import {
    Button,
    InputControl,
    Loading,
    Picker,
    Surface,
    TextInput,
    useThemedStyle,
    type Form as FormComponent,
    type StyleFunction,
} from "@venuepos/react-common";
import { GQLayoutEntitySave } from "graphql-sdk";
import { useTranslation } from "react-i18next";
import { CustomItemSubForm } from "./custom-item-sub-form";
import { BuyAccountFundsSubForm } from "./buy-account-funds-sub-form";

export function Form(props: {
    form: FormComponent<GQLayoutEntitySave>;
    onSubmit: () => void;
    submitButton: [string, string];
    isEditing?: boolean;
}) {
    const [t] = useTranslation();
    const [{ values, errors }, { setValue, handleSubmit }] = props.form;
    const styles = useThemedStyle(styleFunc);

    const handleOnFunctionChange = useCallback(
        (value: string) => {
            setValue("function", value);
        },
        [setValue]
    );

    const handleOnTypeChange = useCallback(
        (value: string) => {
            setValue("type", value);
        },
        [setValue]
    );

    if (!values) {
        return <Loading />;
    }

    return (
        <>
            <Surface style={styles.surfaceContainer}>
                <InputControl error={errors.name}>
                    <TextInput
                        label={t("common.name", "Name")}
                        placeholder={t(
                            "backoffice.common.enter_name",
                            "Enter name"
                        )}
                        defaultValue={values.name || ""}
                        onChangeText={text => setValue("name", text)}
                    />
                </InputControl>

                <InputControl
                    error={errors.type}
                    description={t(
                        "backoffice.layout_entities.type_description",
                        "Type of button"
                    )}
                >
                    <Picker
                        label={t(
                            "backoffice.layout_entities.type.label",
                            "Type"
                        )}
                        onValueChange={handleOnTypeChange}
                        selectedValue={values.type}
                        disabled={props.isEditing}
                    >
                        <Picker.Item
                            key="__SELECT__"
                            label={t(
                                "backoffice.layout_entities.select_type",
                                "Select type"
                            )}
                            value=""
                        />

                        <Picker.Item
                            key="function"
                            label={t(
                                "backoffice.layout_entities.function.label",
                                "Function"
                            )}
                            value="FUNCTION"
                        />
                    </Picker>
                </InputControl>

                <InputControl
                    error={errors.function}
                    description={t(
                        "backoffice.layout_entities.function_description",
                        "Function of button"
                    )}
                >
                    <Picker
                        label={t(
                            "backoffice.layout_entities.function.label",
                            "Function"
                        )}
                        onValueChange={handleOnFunctionChange}
                        selectedValue={values.function}
                        disabled={props.isEditing}
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.layout_entities.select_function",
                                "Select function"
                            )}
                            value=""
                        />

                        <Picker.Item
                            key={"custom_item"}
                            label={t(
                                "backoffice.layout_entities.custom_item.label",
                                "Custom item"
                            )}
                            value="CUSTOM_ITEM"
                        />
                        <Picker.Item
                            key={"buy_account_funds"}
                            label={t(
                                "backoffice.layout_entities.buy_account_funds.label",
                                "Buy account funds"
                            )}
                            value="BUY_ACCOUNT_FUNDS"
                        />
                    </Picker>
                </InputControl>

                {values.function === "CUSTOM_ITEM" && (
                    <CustomItemSubForm values={values} setValue={setValue} />
                )}

                {values.function === "BUY_ACCOUNT_FUNDS" && (
                    <BuyAccountFundsSubForm
                        values={values}
                        setValue={setValue}
                    />
                )}

                <Button onPress={handleSubmit(props.onSubmit)}>
                    {t(props.submitButton[0], props.submitButton[1])}
                </Button>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    surfaceContainer: {
        marginVertical: theme.spacingScale / 2,
    },
});
